/**
 * Date alignment and comparison mode utilities for the Dashboard.
 *
 * Ported from src/YhhsiungGunicornAuthWebapp/static/js/metrics.js
 * Amazon week starts on Sunday and ends on Saturday.
 */

export type AlignmentType = "D" | "W" | "M" | "Q" | "H" | "Y";
export type ComparisonModeType =
  | "YoY"
  | "HoH"
  | "QoQ"
  | "MoM"
  | "WoW"
  | "DoD"
  | "Custom";

/**
 * Comparison modes that produce sensible baselines for each alignment type.
 * "Custom" is always allowed since the user supplies the baseline manually.
 * Order matters: the first entry is the fallback when the current selection
 * becomes invalid after an alignment change.
 */
export const ALLOWED_COMPARISONS: Record<AlignmentType, ComparisonModeType[]> =
  {
    D: ["YoY", "WoW", "DoD", "Custom"],
    W: ["YoY", "WoW", "MoM", "Custom"],
    M: ["YoY", "MoM", "QoQ", "Custom"],
    Q: ["YoY", "QoQ", "HoH", "Custom"],
    H: ["YoY", "HoH", "Custom"],
    Y: ["YoY", "Custom"],
  };

/**
 * Pick a sensible comparison mode for the given alignment. Returns the
 * `current` value unchanged if it's still allowed; otherwise returns the
 * first allowed mode (always "YoY").
 */
export function resolveComparisonForAlignment(
  alignment: AlignmentType,
  current: ComparisonModeType
): ComparisonModeType {
  const allowed = ALLOWED_COMPARISONS[alignment];
  return allowed.includes(current) ? current : allowed[0];
}

/** Parse a YYYY-MM-DD string into a UTC Date. */
function parseDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Format a UTC Date back to YYYY-MM-DD. */
function fmt(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Check if a date is the last day of its month. */
function isLastDayOfMonth(date: Date): boolean {
  const nextDay = new Date(date);
  nextDay.setUTCDate(date.getUTCDate() + 1);
  return nextDay.getUTCMonth() !== date.getUTCMonth();
}

/** Get the first or last day of a period (week/month/quarter/half/year). */
function getFirstOrLastDayOf(
  date: Date,
  dateType: "week" | "month" | "quarter" | "half" | "year",
  getFirst: boolean
): Date {
  const d = new Date(date);
  switch (dateType) {
    case "month":
      if (getFirst) {
        d.setUTCDate(1);
      } else {
        d.setUTCMonth(d.getUTCMonth() + 1, 1);
        d.setUTCDate(d.getUTCDate() - 1);
      }
      break;
    case "week":
      if (getFirst) {
        // Sunday
        d.setUTCDate(d.getUTCDate() - d.getUTCDay());
      } else {
        // Saturday
        d.setUTCDate(d.getUTCDate() - d.getUTCDay() + 6);
      }
      break;
    case "year":
      if (getFirst) {
        d.setUTCMonth(0, 1);
      } else {
        d.setUTCMonth(11, 31);
      }
      break;
    case "quarter": {
      const month = d.getUTCMonth();
      if (month <= 2) {
        // Q1
        d.setUTCMonth(getFirst ? 0 : 2, getFirst ? 1 : 31);
      } else if (month <= 5) {
        // Q2
        d.setUTCMonth(getFirst ? 3 : 5, getFirst ? 1 : 30);
      } else if (month <= 8) {
        // Q3
        d.setUTCMonth(getFirst ? 6 : 8, getFirst ? 1 : 30);
      } else {
        // Q4
        d.setUTCMonth(getFirst ? 9 : 11, getFirst ? 1 : 31);
      }
      break;
    }
    case "half": {
      const hMonth = d.getUTCMonth();
      if (hMonth <= 5) {
        // H1
        d.setUTCMonth(getFirst ? 0 : 5, getFirst ? 1 : 30);
      } else {
        // H2
        d.setUTCMonth(getFirst ? 6 : 11, getFirst ? 1 : 31);
      }
      break;
    }
  }
  return d;
}

/**
 * Align target dates to the boundary of the selected period type.
 *
 * - Daily (D): no change
 * - Weekly (W): Sunday–Saturday (Amazon week starts Sunday)
 * - Monthly (M): 1st–last day of month
 * - Quarterly (Q): Q1 Jan-Mar, Q2 Apr-Jun, Q3 Jul-Sep, Q4 Oct-Dec
 * - Half-Year (H): H1 Jan-Jun, H2 Jul-Dec
 * - Yearly (Y): Jan 1–Dec 31
 */
export function alignDates(
  startDate: string,
  endDate: string,
  alignment: AlignmentType
): { start: string; end: string } {
  if (alignment === "D") {
    return { start: startDate, end: startDate };
  }

  const start = parseDate(startDate);

  const typeMap: Record<
    string,
    "week" | "month" | "quarter" | "half" | "year"
  > = {
    W: "week",
    M: "month",
    Q: "quarter",
    H: "half",
    Y: "year",
  };

  const periodType = typeMap[alignment];
  const newStart = getFirstOrLastDayOf(start, periodType, true);
  const newEnd = getFirstOrLastDayOf(start, periodType, false);

  return { start: fmt(newStart), end: fmt(newEnd) };
}

/**
 * Get Amazon week number and year for a date.
 * Amazon weeks start on Sunday, so we shift +1 day to align with ISO weeks
 * (which start on Monday), then compute the ISO week number.
 */
function getAmazonWeekAndYear(date: Date): { week: number; year: number } {
  const isoDate = new Date(date);
  isoDate.setUTCDate(isoDate.getUTCDate() + 1);
  isoDate.setUTCHours(0, 0, 0, 0);

  // Thursday in current week decides the year
  isoDate.setUTCDate(
    isoDate.getUTCDate() + 3 - ((isoDate.getUTCDay() + 6) % 7)
  );

  // January 4 is always in week 1
  const week1 = new Date(Date.UTC(isoDate.getUTCFullYear(), 0, 4));
  const weekNo =
    1 +
    Math.round(
      ((isoDate.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getUTCDay() + 6) % 7)) /
        7
    );
  return { week: weekNo, year: isoDate.getUTCFullYear() };
}

/**
 * Get the date of a specific day in an Amazon week.
 * dayOffset: 0 = Sunday, 6 = Saturday
 */
function getDateOfAmazonWeek(
  week: number,
  year: number,
  dayOffset: number = 0
): Date {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay();
  if (dayOfWeek <= 4) {
    simple.setUTCDate(simple.getUTCDate() - simple.getUTCDay() + 1);
  } else {
    simple.setUTCDate(simple.getUTCDate() + 8 - simple.getUTCDay());
  }
  // Subtract one day to get to Sunday (Amazon week start)
  simple.setUTCDate(simple.getUTCDate() - 1);
  // Add dayOffset (0 for Sunday, 6 for Saturday)
  simple.setUTCDate(simple.getUTCDate() + dayOffset);
  return simple;
}

/** Subtract months from a date, clamping to end-of-month when needed. */
function subtractMonths(date: Date, months: number): Date {
  const d = new Date(date);
  const day = d.getUTCDate();
  d.setUTCMonth(d.getUTCMonth() - months);
  // If the day overflowed (e.g. Jan 31 - 1 month → Mar 3), clamp to last day
  if (d.getUTCDate() !== day) {
    d.setUTCDate(0);
  }
  return d;
}

/**
 * Compute baseline dates from target dates and a comparison mode.
 *
 * - YoY: -1 year (with Amazon week alignment for Sun-Sat ranges)
 * - HoH: -6 months
 * - QoQ: -3 months
 * - MoM: -1 month
 * - WoW: -7 days
 * - DoD: -1 day
 * - Custom: returns target dates unchanged (caller handles manual input)
 *
 * Month-end adjustment: if target end is last day of month and target start
 * is 1st, baseline end is also adjusted to last day of its month.
 */
export function computeBaselineDates(
  targetStart: string,
  targetEnd: string,
  mode: ComparisonModeType
): { start: string; end: string } {
  if (mode === "Custom") {
    return { start: targetStart, end: targetEnd };
  }

  const tStart = parseDate(targetStart);
  const tEnd = parseDate(targetEnd);

  let bStart: Date;
  let bEnd: Date;

  const rangeDays = (tEnd.getTime() - tStart.getTime()) / (1000 * 60 * 60 * 24);

  switch (mode) {
    case "YoY": {
      if (tStart.getUTCDay() === 0 && tEnd.getUTCDay() === 6) {
        // Amazon week-aligned range: use Amazon week number alignment
        const { week, year } = getAmazonWeekAndYear(tStart);
        bStart = getDateOfAmazonWeek(week, year - 1, 0);
        bEnd = new Date(bStart);
        bEnd.setUTCDate(bStart.getUTCDate() + rangeDays);
      } else {
        bStart = new Date(tStart);
        bEnd = new Date(tEnd);
        bStart.setUTCFullYear(bStart.getUTCFullYear() - 1);
        bEnd.setUTCFullYear(bEnd.getUTCFullYear() - 1);

        // Single-day YoY: align to same day of week
        if (tStart.getTime() === tEnd.getTime()) {
          const targetDow = tStart.getUTCDay();
          const baselineDow = bStart.getUTCDay();
          const shift = (targetDow - baselineDow + 7) % 7;
          if (shift !== 0) {
            bStart.setUTCDate(bStart.getUTCDate() + shift);
          }
          bEnd = new Date(bStart);
        } else if (isLastDayOfMonth(tEnd) && tStart.getUTCDate() === 1) {
          // Month-end adjustment
          bEnd = getFirstOrLastDayOf(bEnd, "month", false);
        }
      }
      break;
    }
    case "HoH": {
      bStart = subtractMonths(new Date(tStart), 6);
      bEnd = subtractMonths(new Date(tEnd), 6);
      if (isLastDayOfMonth(tEnd) && tStart.getUTCDate() === 1) {
        bEnd = getFirstOrLastDayOf(bEnd, "month", false);
      }
      break;
    }
    case "QoQ": {
      bStart = subtractMonths(new Date(tStart), 3);
      bEnd = subtractMonths(new Date(tEnd), 3);
      if (isLastDayOfMonth(tEnd) && tStart.getUTCDate() === 1) {
        bEnd = getFirstOrLastDayOf(bEnd, "month", false);
      }
      break;
    }
    case "MoM": {
      bStart = subtractMonths(new Date(tStart), 1);
      bEnd = subtractMonths(new Date(tEnd), 1);
      if (isLastDayOfMonth(tEnd) && tStart.getUTCDate() === 1) {
        bEnd = getFirstOrLastDayOf(bEnd, "month", false);
      }
      break;
    }
    case "WoW": {
      bStart = new Date(tStart);
      bEnd = new Date(tEnd);
      bStart.setUTCDate(bStart.getUTCDate() - 7);
      bEnd.setUTCDate(bEnd.getUTCDate() - 7);
      break;
    }
    case "DoD": {
      bStart = new Date(tStart);
      bEnd = new Date(tEnd);
      bStart.setUTCDate(bStart.getUTCDate() - 1);
      bEnd.setUTCDate(bEnd.getUTCDate() - 1);
      break;
    }
  }

  return { start: fmt(bStart!), end: fmt(bEnd!) };
}

/** Format a period range into a human label for the Historical Trends columns. */
export function formatPeriodLabel(
  start: string,
  end: string,
  alignment: AlignmentType
): string {
  const s = parseDate(start);
  const e = parseDate(end);
  const mm = (d: Date) => String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = (d: Date) => String(d.getUTCDate()).padStart(2, "0");
  const yyyy = (d: Date) => d.getUTCFullYear();
  const monthShort = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  switch (alignment) {
    case "D":
      return `${mm(s)}/${dd(s)}`;
    case "W":
      return `W/E ${mm(e)}/${dd(e)}`;
    case "M":
      return `${monthShort[s.getUTCMonth()]} ${yyyy(s)}`;
    case "Q": {
      const q = Math.floor(s.getUTCMonth() / 3) + 1;
      return `Q${q} ${yyyy(s)}`;
    }
    case "H": {
      const h = s.getUTCMonth() < 6 ? 1 : 2;
      return `H${h} ${yyyy(s)}`;
    }
    case "Y":
      return `${yyyy(s)}`;
  }
}

/**
 * Step a single period backwards by one unit. The returned (start, end) are
 * already aligned to the period boundary for W/M/Q/H/Y; for D it just
 * subtracts one day.
 */
export function getPriorPeriod(
  start: string,
  end: string,
  alignment: AlignmentType
): { start: string; end: string } {
  const s = parseDate(start);

  switch (alignment) {
    case "D": {
      const d = new Date(s);
      d.setUTCDate(d.getUTCDate() - 1);
      const f = fmt(d);
      return { start: f, end: f };
    }
    case "W": {
      // Step back 7 days from the start; alignDates re-snaps to Sun-Sat.
      const d = new Date(s);
      d.setUTCDate(d.getUTCDate() - 7);
      return alignDates(fmt(d), fmt(d), "W");
    }
    case "M": {
      const d = subtractMonths(s, 1);
      return alignDates(fmt(d), fmt(d), "M");
    }
    case "Q": {
      const d = subtractMonths(s, 3);
      return alignDates(fmt(d), fmt(d), "Q");
    }
    case "H": {
      const d = subtractMonths(s, 6);
      return alignDates(fmt(d), fmt(d), "H");
    }
    case "Y": {
      const d = new Date(s);
      d.setUTCFullYear(d.getUTCFullYear() - 1);
      return alignDates(fmt(d), fmt(d), "Y");
    }
  }
  // unreachable; satisfies the type checker
  return { start, end };
}

/**
 * Build N period ranges ending at `anchorEnd`, walking backwards.
 *
 * Result is ordered oldest → newest so the rightmost column is the most
 * recent period. Each period is aligned to the boundary of `alignment`.
 */
export function getTrailingPeriods(
  anchorEnd: string,
  alignment: AlignmentType,
  n: number
): Array<{ start: string; end: string; label: string }> {
  if (n <= 0) return [];
  // Anchor the most recent period: align using the anchor end date.
  let current = alignDates(anchorEnd, anchorEnd, alignment);
  const periods: Array<{ start: string; end: string; label: string }> = [
    {
      start: current.start,
      end: current.end,
      label: formatPeriodLabel(current.start, current.end, alignment),
    },
  ];
  for (let i = 1; i < n; i++) {
    current = getPriorPeriod(current.start, current.end, alignment);
    periods.push({
      start: current.start,
      end: current.end,
      label: formatPeriodLabel(current.start, current.end, alignment),
    });
  }
  return periods.reverse();
}
